/**
 * React auth context.
 *
 * Wraps the token/user store with hooks so components can:
 *   - useAuth() to read the current user + login/logout helpers
 *   - useRequireAuth() to redirect to /login when unauthenticated
 *
 * Keeps in-sync with localStorage so multiple tabs stay coherent.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  clearTokens,
  getTokens,
  getUser,
  setTokens,
  setUser,
  subscribe,
} from './auth-store';
import { auth as authApi, me as meApi } from './api';
import type { AuthResult, UserPublic } from './types';

type AuthContextValue = {
  user: UserPublic | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  /** Store an already-issued session (A-Roll SaaS email-verify flow,
   *  which returns tokens without a password round-trip). */
  adoptSession: (result: AuthResult) => void;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<UserPublic | null>(() => getUser());

  useEffect(() => {
    const unsub = subscribe(() => {
      setUserState(getUser());
    });
    // Also listen for storage events from other tabs.
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'kg.user' || e.key === 'kg.accessToken') {
        setUserState(getUser());
      }
    };
    window.addEventListener('storage', onStorage);
    // Refresh the user from the server once on mount so newly-added
    // fields (e.g. isOwner) appear without forcing a re-login. Cached
    // user stays put if the refresh fails.
    if (getTokens()) {
      void meApi
        .profile()
        .then((fresh) => {
          setUser(fresh);
          setUserState(fresh);
        })
        .catch(() => {
          /* keep the cached user */
        });
    }
    return () => {
      unsub();
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: !!user && !!getTokens(),
      login: async (email, password) => {
        const result = await authApi.login(email, password);
        setTokens({
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
        });
        setUser(result.user);
        setUserState(result.user);
      },
      adoptSession: (result) => {
        setTokens({
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
        });
        setUser(result.user);
        setUserState(result.user);
      },
      logout: async () => {
        const tokens = getTokens();
        if (tokens?.refreshToken) {
          await authApi.logout(tokens.refreshToken).catch(() => {
            // best-effort: clear local state even if the server call fails
          });
        }
        clearTokens();
        setUserState(null);
      },
    }),
    [user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
